import {isFeatureEnabled} from 'feature-flags';
import {match} from 'ts-pattern';

import {featureFlags} from '@dms/feature-flags';

import {SaneCustomer, SaneCustomerType} from '../types';

export interface SaneCustomerContactDetails {
  name: string;
  email: string;
  phoneNumber: string;
  prefix: string;
}

/**
 * Resolves the contact details to prefill when a customer is picked from
 * {@link CustomerSelectSearch} (`onCustomerSelect`).
 *
 * When the `CUSTOMER_PRIMARY_CONTACT` feature flag is enabled, the customer's
 * primary contact takes precedence — but only for customer-level selections
 * (a company or a physical person). When a specific contact person was picked
 * (`type === 'contactPerson'`), that person's own details are used and the primary
 * contact is never substituted. In every other case the selected person's own
 * details are used. Accepts a partial customer so it can also be reused for
 * create/edit flows that build the customer shape manually.
 */
export function getSaneCustomerContactDetails(
  customer: Partial<SaneCustomer>
): SaneCustomerContactDetails {
  const shouldUsePrimaryContact = isFeatureEnabled(featureFlags.CUSTOMER_PRIMARY_CONTACT);

  if (!shouldUsePrimaryContact) {
    return {
      name: customer.contactPersonName ?? customer.name ?? '',
      email: customer.email ?? '',
      phoneNumber: customer.phoneNumber ?? '',
      prefix: customer.prefix ?? '',
    };
  }

  return match<SaneCustomerType, SaneCustomerContactDetails>(customer.type ?? 'physicalPerson')
    .with('contactPerson', () => ({
      name: customer.contactPersonName ?? customer.name ?? '',
      email: customer.email ?? '',
      phoneNumber: customer.phoneNumber ?? '',
      prefix: customer.prefix ?? '',
    }))
    .with('business', 'physicalPerson', () => ({
      name: customer.primaryContact?.name ?? customer.name ?? '',
      email: customer.primaryContact?.email ?? customer.email ?? '',
      phoneNumber: customer.primaryContact?.phoneNumber ?? customer.phoneNumber ?? '',
      prefix: customer.primaryContact?.prefix ?? customer.prefix ?? '',
    }))
    .exhaustive();
}
